import * as React from "react";
import { TextField, Checkbox, Tooltip, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { observer } from "mobx-react";
import * as _ from "lodash";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import { useRootStore } from "../../../store/contexts";
import { FieldLabelWithHelp } from "./FieldHelpIcon";

export const idForAdding = "add-new";
export const idForSelectAll = "__select_all__";
export const idForSelectNone = "__select_none__";
const idForPinDivider = "__pin_divider__";

export type InputReferenceType = {
  displayId: string;
  id: string;
  inputValue?: string;
};
const filter = createFilterOptions<InputReferenceType>();

export type InputReferenceSetProps = {
  id: string;
  selectedValues?: { displayId: string; id: string }[];
  onChange: (value: string) => void;
  onSelect: (values: { displayId: string; id: string }[]) => void;
  options?: { displayId: string; id: string; [key: string]: any }[];
  optionsMap?: (option: any, backgroundColor: string) => React.ReactElement;
  miniFormMode?: "inline" | "tooltip";
  error?: boolean;
  helperText?: string;
  label?: string;
  icon?: React.ReactElement;
  checkedIcon?: React.ReactElement;
  onView?: (id: string) => void;
  autoFocus?: boolean;
  customWidth?: number;
  disabled?: boolean;
  hidden?: boolean;
  description?: string;
  canCreate?: boolean;
  nullText?: string;
  renderCreateDialog?: (props: {
    open: boolean;
    initialValue: string;
    onCreated: (value: { displayId: string; id: string }) => void;
    onCancelled: () => void;
  }) => React.ReactNode;
  pinnedDisplayIds?: string[];
};

export const InputReferenceSet: React.FC<InputReferenceSetProps> = observer(
  ({
    onChange,
    onSelect,
    selectedValues = [],
    options = [],
    optionsMap,
    miniFormMode = "inline",
    label,
    icon,
    error,
    helperText,
    checkedIcon,
    autoFocus = false,
    customWidth = 500,
    disabled = false,
    hidden = false,
    description,
    canCreate = true,
    nullText,
    renderCreateDialog,
    onView,
    pinnedDisplayIds,
  }) => {
    const theme = useTheme();
    const rootStore = useRootStore();
    const { langStore } = rootStore;
    const [isReadOnly, setIsReadOnly] = React.useState(true);
    const [focused, setFocused] = React.useState(false);
    const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
    const [createDialogInitialValue, setCreateDialogInitialValue] =
      React.useState("");
    const [inputValue, setInputValue] = React.useState("");
    if (hidden) return null;

    const allSelected =
      options.length > 0 && selectedValues.length === options.length;
    const isEmpty = selectedValues.length === 0;
    const showLabelInside = description && isEmpty && !focused;
    const baseLabel =
      nullText && isEmpty && !focused ? `${label} (${nullText})` : label;
    const displayLabel = showLabelInside ? undefined : baseLabel;
    const effectivePlaceholder = showLabelInside
      ? nullText
        ? `${label} (${nullText})`
        : label
      : !label
      ? `${langStore.searchCommand}...`
      : undefined;

    return (
      <>
        <Autocomplete
          disabled={disabled}
          multiple
          disableCloseOnSelect
          options={options}
          value={selectedValues}
          inputValue={inputValue}
          onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
          slotProps={{
            popper: {
              sx: { zIndex: 10300 },
            },
          }}
          isOptionEqualToValue={(option: any, value: any) =>
            option.id === value.id
          }
          onChange={(_event: any, newValue: any[]) => {
            const lastItem =
              newValue.length > 0 ? newValue[newValue.length - 1] : null;
            if (lastItem?.id === idForAdding && renderCreateDialog) {
              const notFound = /".*?"/.exec(lastItem.displayId);
              setCreateDialogInitialValue(
                notFound ? notFound[0].replaceAll('"', "") : ""
              );
              setCreateDialogOpen(true);
              setInputValue("");
              return;
            }
            const filteredValue = newValue.filter(
              (v) =>
                v.id !== idForSelectAll &&
                v.id !== idForSelectNone &&
                v.id !== idForPinDivider
            );
            onSelect(filteredValue);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          sx={{ m: 1, width: customWidth }}
          getOptionLabel={(option) =>
            option.id === idForSelectAll ||
            option.id === idForSelectNone ||
            option.id === idForPinDivider
              ? ""
              : option.displayId
          }
          getOptionDisabled={(option) => option.id === idForPinDivider}
          noOptionsText={`${langStore.noDataMessage}`}
          renderTags={(value: InputReferenceType[], getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              const clickable = !!onView && option.id !== idForAdding;
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  label={option.displayId}
                  size="small"
                  onClick={clickable ? () => onView!(option.id) : undefined}
                  sx={clickable ? { cursor: "pointer" } : undefined}
                />
              );
            })
          }
          renderInput={(params) => {
            const randomId = `ref-${Math.random().toString(36).substr(2, 9)}`;

            return (
              <TextField
                {...params}
                autoFocus={autoFocus}
                size={"small"}
                error={error}
                helperText={helperText}
                label={
                  <FieldLabelWithHelp
                    label={displayLabel}
                    description={description}
                  />
                }
                InputLabelProps={description ? { shrink: true } : undefined}
                onChange={_.debounce(
                  (event) => onChange(event.target.value),
                  500
                )}
                placeholder={effectivePlaceholder}
                inputProps={{
                  ...params.inputProps,
                  readOnly: isReadOnly,
                  autoComplete: "one-time-code",
                  name: randomId,
                  id: randomId,
                  "data-form-type": "other",
                  onFocus: (e: any) => {
                    setIsReadOnly(false);
                    e.target.removeAttribute("readonly");
                    params.inputProps.onFocus?.(e);
                  },
                  onBlur: (e: any) => {
                    setIsReadOnly(true);
                    e.target.setAttribute("readonly", "readonly");
                    params.inputProps.onBlur?.(e);
                  },
                }}
              />
            );
          }}
          filterOptions={(opts, params) => {
            const filtered = filter(opts, params);
            const { inputValue } = params;

            if (canCreate && opts.length === 0 && inputValue !== "") {
              return [
                {
                  id: idForAdding,
                  inputValue,
                  displayId: `${langStore.addCommand} "${inputValue}"`,
                },
              ];
            }

            const actions: InputReferenceType[] = [];
            if (inputValue === "" && filtered.length > 0) {
              if (!allSelected) {
                actions.push({
                  id: idForSelectAll,
                  displayId: langStore.selectAllShort,
                });
              }
              if (selectedValues.length > 0) {
                actions.push({
                  id: idForSelectNone,
                  displayId: langStore.selectNoneShort,
                });
              }
            }

            let arranged: InputReferenceType[] = filtered;
            if (
              pinnedDisplayIds &&
              pinnedDisplayIds.length > 0 &&
              filtered.length > 0
            ) {
              const pinSet = new Set(pinnedDisplayIds);
              const pinned: InputReferenceType[] = [];
              const rest: InputReferenceType[] = [];
              for (const f of filtered) {
                if (pinSet.has(f.displayId)) pinned.push(f);
                else rest.push(f);
              }
              if (pinned.length > 0 && rest.length > 0) {
                arranged = [
                  ...pinned,
                  { id: idForPinDivider, displayId: "" },
                  ...rest,
                ];
              } else if (pinned.length > 0) {
                arranged = pinned;
              }
            }

            if (actions.length > 0) {
              return [...actions, ...arranged];
            }
            return arranged;
          }}
          renderOption={(props, option, { inputValue, selected }) => {
            if (option.id === idForPinDivider) {
              const { key } = props as any;
              return (
                <li
                  key={key ?? option.id}
                  aria-hidden="true"
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: "4px 0",
                    borderTop: "1px solid " + theme.palette.divider,
                    pointerEvents: "none",
                    height: 0,
                  }}
                />
              );
            }
            if (option.id === idForSelectAll || option.id === idForSelectNone) {
              const { key, ...restProps } = props;
              const isSelectAll = option.id === idForSelectAll;
              const label = isSelectAll
                ? langStore.selectAllShort
                : langStore.selectNoneShort;
              const handleClick = () => {
                if (isSelectAll) {
                  onSelect(
                    options.map((x) => ({ displayId: x.displayId, id: x.id }))
                  );
                } else {
                  onSelect([]);
                }
              };
              return (
                <li
                  key={option.id}
                  className={restProps.className}
                  style={{
                    ...restProps.style,
                    cursor: "pointer",
                    color: isSelectAll
                      ? theme.palette.primary.main
                      : theme.palette.error.main,
                    fontWeight: 500,
                    borderBottom:
                      option.id === idForSelectNone
                        ? "1px solid " + theme.palette.divider
                        : undefined,
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClick();
                  }}
                >
                  {label}
                </li>
              );
            }

            const matches = match(option.displayId, inputValue, {
              insideWords: true,
            });
            const parts = parse(option.displayId, matches);
            const index = props.id ? +props.id.split("-").slice(-1) % 2 : 1;
            const backgroundColor =
              index === 0
                ? theme.palette.background.paper
                : theme.palette.action.hover;
            let extraStyle = {};
            const adding = option.id === idForAdding;
            if (adding) {
              extraStyle = { color: "darkGreen", fontWeight: 700 };
            }

            const baseContent = (
              <div>
                {parts.map((part, idx) => (
                  <span
                    key={idx}
                    style={{
                      fontWeight: part.highlight ? 700 : 400,
                      ...extraStyle,
                    }}
                  >
                    {part.text}
                  </span>
                ))}
              </div>
            );

            let optionContent;
            if (adding) {
              optionContent = baseContent;
            } else if (optionsMap && miniFormMode === "tooltip") {
              optionContent = (
                <Tooltip
                  title={optionsMap(option, theme.palette.background.paper)}
                  placement="right"
                  arrow
                  slotProps={{
                    popper: {
                      sx: { zIndex: 10300 },
                    },
                    tooltip: {
                      sx: { maxWidth: "none" },
                    },
                  }}
                >
                  {baseContent}
                </Tooltip>
              );
            } else if (optionsMap && miniFormMode === "inline") {
              optionContent = optionsMap(option, backgroundColor);
            } else {
              optionContent = baseContent;
            }

            return (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8, ...extraStyle }}
                  checked={selected}
                />
                {optionContent}
              </li>
            );
          }}
        />
        {renderCreateDialog &&
          renderCreateDialog({
            open: createDialogOpen,
            initialValue: createDialogInitialValue,
            onCreated: (value) => {
              setCreateDialogOpen(false);
              onSelect([...selectedValues, value]);
            },
            onCancelled: () => {
              setCreateDialogOpen(false);
            },
          })}
      </>
    );
  }
);
